import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from './AuthContext';
import { useDataCache } from './DataCacheProvider';

export const SubscriptionContext = createContext();

export function SubscriptionProvider({ children }) {
  const { token } = useContext(AuthContext);
  const cache = useDataCache();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Prevent duplicate calls
  const fetchingRef = useRef(false);
  const lastTokenRef = useRef(null);

  useEffect(() => {
    if (!token) {
      setSubscriptionData(null);
      setLoading(false);
      return;
    }

    // Skip if already fetching or token hasn't changed
    if (fetchingRef.current && lastTokenRef.current === token) {
      return;
    }

    const fetchSubscription = async () => {
      // Prevent concurrent fetches
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      lastTokenRef.current = token;

      try {
        // Cache for 5 minutes (300,000ms)
        const cacheKey = `subscription:${token}`;
        const data = await cache.use(
          cacheKey,
          async () => {
            //console.log('[SubscriptionContext] Fetching subscription status');
            const res = await fetch(
              `${process.env.REACT_APP_API_URL}/billing/subscription-status`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) throw new Error('Failed to fetch subscription');
            return res.json();
          },
          { ttlMs: 900_000 } // 5 minutes
        );
        setSubscriptionData(data);
      } catch (err) {
        //console.error('[SubscriptionContext] Failed to fetch subscription:', err);
        setSubscriptionData(null);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchSubscription();
  }, [token, cache]);

  // Derive values from subscription data
  const hasActiveSubscription = subscriptionData?.has_active_subscription || false;
  const status = subscriptionData?.status || 'none';
  const trialDaysRemaining = subscriptionData?.trial_days_remaining || 0;
  
  // Normalize trialing status
  const isTrialing = status === 'trialing' || 
    (subscriptionData?.trial_used && trialDaysRemaining > 0);
  
  const isPastDue = status === 'past_due';
  const isFirstTimeUser = subscriptionData?.is_first_time_user || false;

  // Manual refresh function
  const refresh = async () => {
    if (!token || fetchingRef.current) return;
    
    // Clear cache for this key
    const cacheKey = `subscription:${token}`;
    if (cache.invalidate) {
      cache.invalidate(cacheKey);
    }
    
    setLoading(true);
    fetchingRef.current = true;

    try {
      //console.log('[SubscriptionContext] Refreshing subscription status');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/billing/subscription-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to fetch subscription');
      const data = await res.json();
      setSubscriptionData(data);
      
      // Update cache
      if (cache.set) {
        cache.set(cacheKey, data, { ttlMs: 300_000 });
      }
    } catch (err) {
      console.error('[SubscriptionContext] Refresh failed:', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  const value = {
    hasActiveSubscription,
    isTrialing,
    isPastDue,
    trialDaysRemaining,
    isFirstTimeUser,
    subscriptionData,
    loading,
    refresh,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// Custom hook
export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}